'use client';

import { useState, useRef, useEffect } from 'react'
import { translations } from '@/lib/translations'
import { Send, Mail, Phone, MapPin, Clock, CheckCircle, AlertCircle, Loader2 } from 'lucide-react'
import contactImage from '../lib/asset/partiners/et.png'

import { api } from '@/lib/api'

export default function ContactSection({ language }) {
  const t = translations[language]
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  })
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState({ type: '', text: '' })
  const [focusedField, setFocusedField] = useState('')
  const formRef = useRef(null)

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setMessage({ type: '', text: '' })

    try {
      await api.post('/contact', formData)
      if (true) {
        setMessage({ type: 'success', text: t.contact.success })
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
        })
        // Scroll to message
        setTimeout(() => {
          formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }, 100)
      } else {
        setMessage({ type: 'error', text: t.contact.error })
      }
    } catch (error) {
      console.error('Error sending message:', error)
      setMessage({ type: 'error', text: t.contact.error })
    } finally {
      setLoading(false)
    }
  }

  // Contact info items
  const contactInfo = [
    { icon: Mail, label: 'Email', value: 'fortinotech@gmail.com', href: 'mailto:fortinotech@gmail.com' },
    { icon: Phone, label: 'Phone', value: '+251 979 235 206', href: 'tel:+251979235206' },
    { icon: MapPin, label: 'Address', value: t.contact.address || 'Addis Ababa, Jemo Michael, Peony Commercial Building, Office No. 306/A', href: 'https://www.google.com/maps?q=Peony+Commercial+Building+Jemo+Michael+Addis+Ababa' },
     { icon: Clock, label: 'Business Hours', value: '24/7' },
  ]

  return (
    <section
      id="contact"
      className="relative py-4 md:py-4 lg:py-4 px-4 overflow-hidden "
    >
      {/* Premium Background Effects */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-gradient-to-r from-primary/5 to-secondary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-gradient-to-t from-primary/5 to-transparent rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-tr from-primary/3 via-transparent to-secondary/3 rounded-full blur-3xl" />
      </div>

      {/* Decorative Grid Pattern */}
      <div 
        className="absolute inset-0 opacity-[0.02] pointer-events-none"
        style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, currentColor 1px, transparent 1px)`,
          backgroundSize: '48px 48px'
        }}
      />

      <div className="relative max-w-7xl mx-auto z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          
          {/* Left Column - Contact Info & Illustration */}
          <div className="space-y-8">
            {/* Section Header */}
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/5 border border-primary/10 backdrop-blur-sm">
                <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                <span className="text-sm font-medium text-primary/80 uppercase tracking-wide">
                  Get In Touch
                </span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold">
                <span className="bg-gradient-to-r from-primary via-primary/80 to-secondary bg-clip-text text-transparent">
                  {t.contact.title}
                </span>
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed">
                Have a project in mind? We'd love to hear from you. Send us a message and we'll respond within 24 hours.
              </p>
            </div>

            {/* Contact Info Cards */}
            <div className="grid sm:grid-cols-2 gap-4">
              {contactInfo.map((info, index) => {
                const Icon = info.icon
                return (
                  <div
                    key={index}
                    className="group p-4 rounded-xl bg-card/50 backdrop-blur-sm border border-border/50 hover:border-primary/30 hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
                  >
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-lg bg-primary/10 group-hover:bg-primary/20 transition-colors">
                        <Icon className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wide">{info.label}</p>
                        {info.href ? (
                          <a
                            href={info.href}
                            className="text-foreground font-medium hover:text-primary transition-colors text-sm"
                            target={info.label === 'Address' ? '_blank' : undefined}
                            rel="noopener noreferrer"
                          >
                            {info.value}
                          </a>
                        ) : (
                          <p className="text-foreground font-medium text-sm">{info.value}</p>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>

            <div className="relative mt-8 rounded-3xl overflow-hidden border border-border/50 shadow-xl h-64">
              <iframe
                title="Fortino Tech office location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3941.064570732888!2d38.7200798!3d8.9661978!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x164b812def44429d%3A0x970684d96e758e03!2sFortino%20Tech!5e0!3m2!1sen!2set!4v1787550285753!5m2!1sen!2set"
                className="w-full h-full border-0"
                loading="lazy"
                allowFullScreen
              />
            </div>
            {/* Illustration */}
          </div>

          {/* Right Column - Contact Form */}
          <div ref={formRef} className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-3xl blur-2xl opacity-30" />
            <div className="relative bg-card/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-border/50 p-6 sm:p-8 md:p-10">
              {/* Form Header */}
              <div className="mb-8 text-center lg:text-left">
                <h3 className="text-2xl font-bold text-foreground mb-2">Send us a Message</h3>
                <p className="text-muted-foreground text-sm">
                  Fill out the form below and we'll get back to you shortly
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Name & Email Row */}
                <div className="grid sm:grid-cols-2 gap-5">
                  {/* Name Field */}
                  <div className="relative">
                    <label htmlFor="name" className="block text-sm font-medium text-foreground mb-2">
                      {t.contact.form.name} <span className="text-primary">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      onFocus={() => setFocusedField('name')}
                      onBlur={() => setFocusedField('')}
                      required
                      className={`w-full px-4 py-3 bg-background/50 border rounded-xl text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 transition-all ${
                        focusedField === 'name'
                          ? 'border-primary ring-primary/20'
                          : 'border-border hover:border-primary/30'
                      }`}
                      placeholder=" Naol Berhanu"
                    />
                  </div>

                  {/* Email Field */}
                  <div className="relative">
                    <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                      {t.contact.form.email} <span className="text-primary">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      onFocus={() => setFocusedField('email')}
                      onBlur={() => setFocusedField('')}
                      required
                      className={`w-full px-4 py-3 bg-background/50 border rounded-xl text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 transition-all ${
                        focusedField === 'email'
                          ? 'border-primary ring-primary/20'
                          : 'border-border hover:border-primary/30'
                      }`}
                      placeholder="name@example.com"
                    />
                  </div>
                </div>

                {/* Phone & Subject Row */}
                <div className="grid sm:grid-cols-2 gap-5">
                  {/* Phone Field */}
                  <div className="relative">
                    <label htmlFor="phone" className="block text-sm font-medium text-foreground mb-2">
                      {t.contact.form.phone} <span className="text-primary">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      onFocus={() => setFocusedField('phone')}
                      onBlur={() => setFocusedField('')}
                      required
                      className={`w-full px-4 py-3 bg-background/50 border rounded-xl text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 transition-all ${
                        focusedField === 'phone'
                          ? 'border-primary ring-primary/20'
                          : 'border-border hover:border-primary/30'
                      }`}
                      placeholder="+251 911 123 456"
                    />
                  </div>

                  {/* Subject Field */}
                  <div className="relative">
                    <label htmlFor="subject" className="block text-sm font-medium text-foreground mb-2">
                      {t.contact.form.subject} <span className="text-primary">*</span>
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      onFocus={() => setFocusedField('subject')}
                      onBlur={() => setFocusedField('')}
                      required
                      className={`w-full px-4 py-3 bg-background/50 border rounded-xl text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 transition-all ${
                        focusedField === 'subject'
                          ? 'border-primary ring-primary/20'
                          : 'border-border hover:border-primary/30'
                      }`}
                      placeholder="Project Inquiry"
                    />
                  </div>
                </div>

                {/* Message Field */}
                <div className="relative">
                  <label htmlFor="message" className="block text-sm font-medium text-foreground mb-2">
                    {t.contact.form.message} <span className="text-primary">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('message')}
                    onBlur={() => setFocusedField('')}
                    required
                    rows={5}
                    className={`w-full px-4 py-3 bg-background/50 border rounded-xl text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 transition-all resize-none ${
                      focusedField === 'message'
                        ? 'border-primary ring-primary/20'
                        : 'border-border hover:border-primary/30'
                    }`}
                    placeholder="Tell us about your project..."
                  />
                </div>

                {/* Message Display */}
                {message.text && (
                  <div
                    className={`flex items-center gap-3 p-4 rounded-xl text-sm font-medium transition-all ${
                      message.type === 'success'
                        ? 'bg-green-50 text-green-800 dark:bg-green-900/30 dark:text-green-200 border border-green-200 dark:border-green-800'
                        : 'bg-red-50 text-red-800 dark:bg-red-900/30 dark:text-red-200 border border-red-200 dark:border-red-800'
                    }`}
                  >
                    {message.type === 'success' ? (
                      <CheckCircle className="w-5 h-5 flex-shrink-0" />
                    ) : (
                      <AlertCircle className="w-5 h-5 flex-shrink-0" />
                    )}
                    <span>{message.text}</span>
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="group relative w-full py-4 bg-gradient-to-r from-primary to-secondary text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 overflow-hidden"
                >
                  <span className="relative z-10 flex items-center justify-center gap-2">
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        {t.contact.sending}
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                        {t.contact.send}
                      </>
                    )}
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-secondary to-primary opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </button>
              </form>

              {/* Trust Badge */}
              <div className="mt-6 pt-4 text-center border-t border-border/50">
                <p className="text-xs text-muted-foreground">
                  We respect your privacy. Your information is secure and will never be shared.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Animations */}
      <style jsx>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 0.4;
          }
          50% {
            opacity: 1;
          }
        }
        
        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
      `}</style>
    </section>
  )
}